/*
 * /developers — the public description of the HolidayHive API (DESIGN-api-and-webhooks.md,
 * Rob 25/10/2026: layout A — contents down the side, a list at the top on a phone — and
 * every example in curl AND PowerShell).
 *
 * ⚠️ **THE ADDRESSES COME FROM `lib/apiReference.js`**, and a backend test fails if that list
 * and the real API disagree. Write a new address there, never straight into this page.
 *
 * ⚠️ **THE BASE ADDRESS IS THIS INSTALLATION'S OWN** (`REACT_APP_BACKEND_URL`, the one the app
 * itself talks to), so staging's page shows staging and live shows live — a developer who
 * copies an example gets an address that works where they read it.
 *
 * Served on both hosts, like Terms and Privacy, inside the marketing site's own top bar and
 * footer. Light only, like the landing page.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Download } from 'lucide-react';
import { MarketingNav, MarketingFooter } from '../components/MarketingChrome';
import {
  ENDPOINTS, ERRORS, SECTIONS, SIGNATURE_CHECK, TICKS, WEBHOOK_EVENTS, WEBHOOK_EXAMPLE, WEBHOOK_HEADERS, requestFor,
  retryExample,
} from '../lib/apiReference';

/* Reading first, then changing leave (step 3), then managing people (step 4) — each address
 * says its own method; a change is anything that isn't a GET, grouped by the tick it needs. */
const READS = ENDPOINTS.filter((e) => e.method === 'GET');
const WRITES = ENDPOINTS.filter((e) => e.method !== 'GET' && e.tick === 'book');
const PEOPLE_WRITES = ENDPOINTS.filter((e) => e.method !== 'GET' && e.tick === 'manage_people');
/* Step 5 (26/10/2026): allowances, time in lieu and leave types. */
const ALLOWANCE_WRITES = ENDPOINTS.filter((e) => e.method !== 'GET' && e.tick === 'manage_allowances');

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const BASE = `${BACKEND}/api`;

const LANGS = [
  { id: 'curl', label: 'curl' },
  { id: 'powershell', label: 'PowerShell' },
];

function Code({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* nothing to copy to: the text is selectable anyway */ }
  };
  return (
    <div className="relative my-3">
      <pre className="overflow-x-auto rounded-lg bg-hive-navy p-4 pr-12 text-[12.5px] leading-relaxed text-[#f2ede4]"
           data-testid={testid}><code>{text}</code></pre>
      <button type="button" onClick={copy} aria-label="Copy"
              className="absolute right-2 top-2 rounded-md bg-white/10 p-1.5 text-white/80 hover:bg-white/20">
        {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  );
}

function LangPicker({ lang, setLang }) {
  return (
    <div className="inline-flex gap-1 rounded-lg bg-hive-cream p-1" role="tablist" aria-label="Examples in">
      {LANGS.map((l) => (
        <button key={l.id} type="button" role="tab" aria-selected={lang === l.id}
                onClick={() => setLang(l.id)} data-testid={`dev-lang-${l.id}`}
                className={`rounded-md px-3 py-1 text-[13px] font-semibold ${lang === l.id ? 'bg-hive-navy text-white' : 'text-hive-muted'}`}>
          {l.label}
        </button>
      ))}
    </div>
  );
}

/* The method and the address. GET in green, POST in the page’s own orange, PUT in
 * indigo, PATCH in blue, DELETE in red. */
const METHOD_COLOURS = {
  GET: 'bg-green-100 text-green-800', POST: 'bg-amber-100 text-amber-800',
  PUT: 'bg-indigo-100 text-indigo-800', PATCH: 'bg-sky-100 text-sky-800', DELETE: 'bg-red-100 text-red-800',
};
function Address({ e }) {
  return (
    <p className="mt-2 break-all font-mono text-[14px]">
      <span className={`mr-2 rounded px-1.5 py-0.5 text-[12px] font-bold ${METHOD_COLOURS[e.method] || METHOD_COLOURS.GET}`}>
        {e.method}
      </span>
      {e.path}
    </p>
  );
}

const H2 = ({ id, children }) => (
  <h2 id={id} className="mt-12 scroll-mt-24 font-poppins text-[22px] font-bold text-hive-navy first:mt-0">{children}</h2>
);
const H3 = ({ id, children }) => (
  <h3 id={id} className="mt-8 scroll-mt-24 font-poppins text-[17px] font-semibold text-hive-navy">{children}</h3>
);
const P = ({ children }) => <p className="mt-2 text-[15px] leading-relaxed text-hive-navy/90">{children}</p>;
const C = ({ children }) => <code className="rounded bg-hive-cream px-1.5 py-0.5 text-[13px]">{children}</code>;

/* `text` with `backticks` shown as code. */
function Words({ text }) {
  return text.split(/(`[^`]+`)/).map((bit, i) => (bit.startsWith('`') && bit.endsWith('`')
    ? <C key={i}>{bit.slice(1, -1)}</C> : <React.Fragment key={i}>{bit}</React.Fragment>));
}

/* One change: its address, words, tick, what to send and a made-up request and reply. */
function WriteEndpoint({ e, lang }) {
  return (
    <section className="mt-2 border-t border-hive-line pt-2" data-testid={`dev-endpoint-${e.id}`}>
      <H3 id={e.id}>{e.title}</H3>
      <Address e={e} />
      <P><Words text={e.what} /></P>
      <p className="mt-1 text-[13px] text-hive-muted">Needs: {TICKS[e.tick]}</p>
      {e.fields && e.fields.length > 0 && (
        <>
          <p className="mt-3 text-[14px] font-semibold">What to send (JSON)</p>
          <ul className="mt-1 space-y-1 text-[14px]">
            {e.fields.map((f) => (
              <li key={f.name}><C>{f.name}</C>{f.required ? <strong> (needed)</strong> : ''} — <Words text={f.says} /></li>
            ))}
          </ul>
        </>
      )}
      <Code text={requestFor(lang, BASE, e.example, e.method, e.body)} />
      <p className="text-[13px] text-hive-muted">What comes back (made-up example):</p>
      <Code text={JSON.stringify(e.reply, null, 2)} />
    </section>
  );
}

export default function DevelopersPage() {
  const [lang, setLang] = useState('curl');
  const sections = SECTIONS.map((s) => {
    if (s.id === 'reference') return { ...s, items: READS.map((e) => ({ id: e.id, label: e.title })) };
    if (s.id === 'changing') return { ...s, items: [...s.items, ...WRITES.map((e) => ({ id: e.id, label: e.title }))] };
    if (s.id === 'managing') return { ...s, items: [...s.items, ...PEOPLE_WRITES.map((e) => ({ id: e.id, label: e.title }))] };
    if (s.id === 'allowances') return { ...s, items: [...s.items, ...ALLOWANCE_WRITES.map((e) => ({ id: e.id, label: e.title }))] };
    return s;
  });

  const contents = (
    <nav aria-label="On this page" className="text-[13.5px]">
      {sections.map((s) => (
        <div key={s.id} className="mb-4">
          <p className="mb-1 font-poppins text-[11.5px] font-semibold uppercase tracking-wide text-hive-orange">{s.label}</p>
          <ul>
            {s.items.map((i) => (
              <li key={i.id}><a href={`#${i.id}`} className="block py-0.5 text-hive-muted hover:text-hive-navy">{i.label}</a></li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    /* ⚠️ `overflow-x-clip`, NOT `overflow-hidden` (Rob, 25/10/2026: the contents scrolled away).
       The footer's coloured circles need clipping at the sides, but `overflow-hidden` makes
       this div a scroll container, and a `sticky` child then sticks to IT — which never
       scrolls — instead of to the window. `clip` cuts the circles off without doing that. */
    <div className="min-h-screen overflow-x-clip bg-hive-warm font-nunito text-base text-hive-navy">
      <MarketingNav />
      <main className="mx-auto w-full max-w-6xl px-4 pb-16 pt-4 sm:px-8" data-testid="developers-page">
        <div className="lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-8">
          <aside className="hidden lg:block">
            {/* Stays in view while the page scrolls; scrolls itself if the window is short. */}
            <div className="sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto pr-2"
                 data-testid="dev-contents">{contents}</div>
          </aside>

          <div className="rounded-2xl border border-hive-line bg-white p-6 shadow-sm sm:p-10">
            <h1 className="font-poppins text-[30px] font-bold">The HolidayHive API</h1>
            <P>
              Read your company’s people and leave from another system, and change them from it: book and approve
              leave, add and update people and departments, and set allowances, time in lieu and leave types. For
              example, to show who’s off in your project tool, or to keep your HR system in step — and have it told
              the moment a booking changes, with <a href="#webhooks" className="text-hive-orange hover:underline">webhooks</a>.
              There’s nothing extra to pay.
            </P>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <LangPicker lang={lang} setLang={setLang} />
              <a href={`${BASE}/v1/openapi.json`} download="holidayhive-openapi.json"
                 className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-hive-orange hover:underline"
                 data-testid="dev-openapi">
                <Download className="h-4 w-4" aria-hidden="true" /> Download the OpenAPI file
              </a>
            </div>

            {/* On a phone the contents are a list here instead of down the side. */}
            <details className="mt-6 rounded-lg border border-hive-line p-4 lg:hidden">
              <summary className="cursor-pointer font-semibold">On this page</summary>
              <div className="mt-3">{contents}</div>
            </details>

            <H2 id="getting-a-key">Getting a key</H2>
            <P>
              An admin makes one in <strong>Settings → Integrations → API keys</strong>: they name it (“Hive feed”) and
              tick what it can read, and what it can change: book and approve leave, manage people and departments,
              or manage allowances and leave types. The key is shown once, when it’s made.
              We only keep a scrambled copy that can check it, so if it’s lost, revoke it and make another. Treat it
              like a password: anyone who has it can do what it was ticked for, for the whole company.
            </P>
            <P>
              A key stops working the moment it’s revoked, and also if the admin who made it leaves or stops being an
              admin. Making and revoking keys is listed in the company’s Settings → Security → Sign-ins.
            </P>

            <H2 id="first-request">Your first request</H2>
            <P>Send the key in the <C>Authorization</C> header. Everything lives under:</P>
            <Code text={`${BASE}/v1`} testid="dev-base" />
            <P>This asks which key you’re using, and is a good first check:</P>
            <Code text={requestFor(lang, BASE, '/v1/key')} testid="dev-first" />
            {lang === 'curl'
              ? <P>Put your key in <C>HH_KEY</C> first (for example <C>export HH_KEY=hh_live_…</C>).</P>
              : <P>Paste your key between the quotes on the first line, then run both lines.</P>}

            <H2 id="never-sent">What we never send</H2>
            <div className="mt-3 rounded-xl border border-hive-line bg-hive-cream p-4 text-[15px]">
              <strong>Whatever a key has ticked</strong>, it never sends leave reasons, decline notes, the names of
              private leave types, IP addresses or sign-in history. Nor anybody’s date of birth, payroll reference,
              phone numbers, home address or emergency contacts: a key that manages people can send those in, but
              they never come back out. The same goes for what anybody writes with time in lieu. Private leave types travel as <C>Time off</C>,
              and so does sickness, unless the key was made with “Show sickness as sickness” ticked. Sickness is
              health data, so only tick that if the system using the key needs it.
            </div>
            <P>
              Only <strong>approved</strong> leave is included. Requests waiting for approval aren’t, so nothing
              shows somebody as off for leave that then gets declined. The one exception: a key that can book and
              approve leave may ask <C>/v1/bookings</C> for waiting, declined or cancelled bookings, because it has to
              be able to find what’s waiting. Who’s off is always approved leave only.
            </P>

            <H2 id="limits">Limits</H2>
            <P>
              Each key can make <strong>5 requests a second</strong>. Every reply says how many are left, in{' '}
              <C>X-Rate-Limit-Remaining</C>. Go over and you’ll get a <C>429</C> with a <C>Retry-After</C> header
              saying how many seconds to wait. Send requests one after another rather than all at once.
            </P>

            <H2 id="dates">Dates and amounts</H2>
            <P>
              Dates are written year-month-day, like <C>2026-11-02</C>, and moments in UTC, like{' '}
              <C>2026-10-20T09:14:02Z</C>. (The app’s own screens show 02/11/2026; this is the form other systems
              expect.)
            </P>
            <P>
              Part days are <C>full_day</C>, <C>morning</C> or <C>afternoon</C>, or a time range like{' '}
              <C>09:00-11:00</C> for leave booked by the hour. Amounts are in each person’s own unit: days, or hours
              for leave booked in hours and for people counted in hours.
            </P>

            <H2 id="errors">Errors</H2>
            <P>Every error looks the same, with a sentence you can show to a person:</P>
            <Code text={'{\n  "error": {\n    "code": "not_ticked",\n    "message": "This key can\'t read balances. An admin can make a key with \\"Balances\\" ticked in Settings → Integrations."\n  }\n}'} />
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-[14px]">
                <thead><tr className="border-b border-hive-line text-hive-muted"><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Code</th><th className="py-2">Means</th></tr></thead>
                <tbody>
                  {ERRORS.map(([status, code, means]) => (
                    <tr key={code} className="border-b border-hive-line align-top">
                      <td className="py-2 pr-3 font-mono">{status}</td>
                      <td className="py-2 pr-3 font-mono text-[12.5px]">{code}</td>
                      <td className="py-2"><Words text={means} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <H2 id="pages">Pages of results</H2>
            <P>
              Lists come back as <C>{'{"data": [...], "next": …}'}</C>. When <C>next</C> isn’t <C>null</C>, there’s
              more: ask again with <C>after=</C> and that value. <C>limit</C> sets how many to a page, up to 500.
            </P>

            <h2 className="mt-12 font-poppins text-[22px] font-bold">Reading</h2>
            {READS.map((e) => (
              <section key={e.id} className="mt-2 border-t border-hive-line pt-2" data-testid={`dev-endpoint-${e.id}`}>
                <H3 id={e.id}>{e.title}</H3>
                <Address e={e} />
                <P><Words text={e.what} /></P>
                <p className="mt-1 text-[13px] text-hive-muted">Needs: {TICKS[e.tick]}</p>
                {e.params.length > 0 && (
                  <ul className="mt-2 space-y-1 text-[14px]">
                    {e.params.map((p) => (
                      <li key={p.name}><C>{p.name}</C>{p.required ? <strong> (needed)</strong> : ''} — <Words text={p.says} /></li>
                    ))}
                  </ul>
                )}
                <Code text={requestFor(lang, BASE, e.example)} />
                <p className="text-[13px] text-hive-muted">What comes back (made-up example):</p>
                <Code text={JSON.stringify(e.reply, null, 2)} />
              </section>
            ))}

            <h2 className="mt-12 font-poppins text-[22px] font-bold">Changing leave</h2>
            <H3 id="changing-leave">How changing leave works</H3>
            <P>
              A key made with <strong>Book and approve leave</strong> ticked can book leave, approve and decline
              requests, cancel bookings and give back days. Everything it does is done <strong>as the admin who made
              the key</strong>, with exactly the checks the app makes for them, and the booking says so in the app:
              “Booked by Rob Hepworth (via API key ‘HR sync’)”.
            </P>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-[15px] text-hive-navy/90">
              <li>A booking is approved straight away, as an admin’s booking is. Send <C>"send_for_approval": true</C> to have it go to the person’s approver instead, as if they had asked.</li>
              <li>The same messages go out as when the admin does it in the app. There’s no way to switch them off.</li>
              <li>A reason (or a decline note) can be sent in. It’s kept, like the box in the app, and never sent back out.</li>
              <li>If HolidayHive says no — not enough allowance, already booked off, a no-leave date — you get a <C>400</C> with the app’s own sentence in <C>message</C>, and nothing is changed.</li>
              <li>Every change answers with the booking as it now stands and a <C>message</C> saying what happened, in words you can show a person.</li>
              <li>Webhooks fire as they would for a change made in the app. <C>by</C> is the admin, with <C>via_api_key</C> set to the key’s name.</li>
            </ul>

            <H3 id="same-request">Retrying safely</H3>
            <P>
              A program that times out can’t tell whether its booking went in. Send an <C>Idempotency-Key</C> header
              with any change: make up a new value for each new change, and send the same value again only when
              retrying that change. Within 24 hours, a retry gets the first answer back (with an{' '}
              <C>Idempotent-Replayed: true</C> header) instead of booking twice. The same value with a different
              request is refused with <C>409</C>. A change that was refused isn’t remembered, so retrying it really
              tries again.
            </P>
            <Code text={retryExample(lang, BASE)} testid="dev-retry" />

            {WRITES.map((e) => <WriteEndpoint key={e.id} e={e} lang={lang} />)}

            <h2 className="mt-12 font-poppins text-[22px] font-bold">Managing people</h2>
            <H3 id="managing-people">How managing people works</H3>
            <P>
              A key made with <strong>Manage people and departments</strong> ticked can add people, change them,
              archive them when they leave and restore them, and add, change and delete empty departments. As with
              leave, everything is done <strong>as the admin who made the key</strong>, through the app’s own code,
              and listed in the company’s activity as “Rob Hepworth (via API key ‘HR sync’)”.
            </P>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-[15px] text-hive-navy/90">
              <li>Everybody added is an employee. A key can’t make anybody an admin, change anybody’s role, or change, archive or restore an admin.</li>
              <li>Nobody can be deleted through the API. Archive them instead; deleting is done in the app.</li>
              <li><strong>Each person added is a person on the company’s bill</strong>, and archiving doesn’t take them off it. The answer says how many people the company is now billed for.</li>
              <li>Changing somebody’s email signs them out everywhere and tells their old address, because the new address is where a password reset goes.</li>
              <li>An email that already belongs to one of your people answers <C>409</C> with their id, so a sync can find them. Ten emails in a day that are registered outside your company pause adding and changing people for 24 hours.</li>
              <li>Departments, locations and managers must be your company’s own: any other id is refused.</li>
              <li><C>Idempotency-Key</C> works here too, and is worth sending with every add.</li>
            </ul>

            {PEOPLE_WRITES.map((e) => <WriteEndpoint key={e.id} e={e} lang={lang} />)}

            <h2 className="mt-12 font-poppins text-[22px] font-bold">Allowances and leave types</h2>
            <H3 id="managing-allowances">How managing allowances works</H3>
            <P>
              A key made with <strong>Manage allowances and leave types</strong> ticked can set a person’s yearly
              allowance, record and correct time in lieu, approve or decline time in lieu claims, and add and change
              leave types. Again, everything is done <strong>as the admin who made the key</strong>, through the
              app’s own code, and listed in the company’s activity as “Rob Hepworth (via API key ‘HR sync’)”.
            </P>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-[15px] text-hive-navy/90">
              <li>A balance is never set directly. It’s always worked out by HolidayHive from the allowance, the bookings and the time in lieu entries, exactly as the app shows it. Read it back with <C>/v1/balances</C>.</li>
              <li>The allowance is in days, as on a person’s record in the app. Carry-over is worked out by HolidayHive at the start of each leave year and can’t be set through the API.</li>
              <li>Time in lieu is never edited or deleted. A mistake is put right with a correction that says why, so the history always adds up.</li>
              <li>A key can’t change an admin’s allowance or time in lieu.</li>
              <li>A key can’t make, change or delete a private or sickness leave type, or touch payroll settings. Annual Leave keeps its name and always counts against the allowance. Leave types can’t be deleted through the API.</li>
              <li>The rules are the app’s own: a time in lieu claim is for time already worked, time in lieu must be switched on for the company, and each leave type needs a name and a colour of its own.</li>
            </ul>

            {ALLOWANCE_WRITES.map((e) => <WriteEndpoint key={e.id} e={e} lang={lang} />)}

            <h2 className="mt-12 font-poppins text-[22px] font-bold">Webhooks</h2>
            <H3 id="webhooks">How webhooks work</H3>
            <P>
              Instead of asking every few minutes, give us an address and we’ll tell it the moment a booking changes.
              An admin adds it in <strong>Settings → Integrations → Webhooks</strong> (up to 5 addresses), ticks what
              it should get, and is shown a <strong>signing secret</strong> once. Each message is a <C>POST</C> of
              JSON to that address, usually within a few seconds.
            </P>
            <P>
              The address must start <C>https://</C>, use the normal port, and be on the public internet: we won’t send
              to anything inside a private network, and we check that again every time we send. We don’t follow
              redirects. Answer with any <C>2xx</C> status within 10 seconds and it counts as delivered.
            </P>
            <P>
              Webhooks carry exactly what the API would: no leave reasons, no decline notes, private leave types as{' '}
              <C>Time off</C>, and sickness as <C>Time off</C> unless the address was ticked “Show sickness as sickness”.
              Using Zapier? A “Catch Hook” in Webhooks by Zapier works (that needs a paid Zapier plan).
            </P>
            <P>
              Webhooks are about <strong>bookings only</strong>. Adding or changing people, departments, allowances,
              time in lieu or leave types sends nothing — whether it’s done in the app or through the API. Use the
              reading addresses above to pick those up.
            </P>

            <H3 id="webhook-events">The events</H3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-[14px]" data-testid="dev-webhook-events">
                <thead><tr className="border-b border-hive-line text-hive-muted"><th className="py-2 pr-3">Event</th><th className="py-2 pr-3">The box an admin ticks</th><th className="py-2">Sent when</th></tr></thead>
                <tbody>
                  {WEBHOOK_EVENTS.map((e) => (
                    <tr key={e.event} className="border-b border-hive-line align-top">
                      <td className="py-2 pr-3 font-mono text-[12.5px] whitespace-nowrap">{e.event}</td>
                      <td className="py-2 pr-3">{e.box}</td>
                      <td className="py-2"><Words text={e.when} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <H3 id="webhook-message">What a message looks like</H3>
            <P>
              The <C>booking</C> is exactly what <C>/v1/bookings/{'{booking_id}'}</C> returns, with the person’s work
              email always filled in, and a <C>status</C> of <C>requested</C>, <C>approved</C>, <C>declined</C> or{' '}
              <C>cancelled</C>. <C>by</C> is who did it, or <C>null</C> when nobody did (approved automatically, or a
              leaver’s bookings cancelled for them); its <C>via_api_key</C> is the key’s name when the change came
              through the API, otherwise <C>null</C>. <C>occurred_at</C> is when it happened; <C>sent_at</C> is this
              try. Headers:
            </P>
            <Code text={WEBHOOK_HEADERS.map(([k, v]) => `${k}: ${v}`).join('\n')} testid="dev-webhook-headers" />
            <p className="text-[13px] text-hive-muted">The body (made-up example):</p>
            <Code text={JSON.stringify(WEBHOOK_EXAMPLE, null, 2)} testid="dev-webhook-example" />

            <H3 id="webhook-signature">Checking it came from us</H3>
            <P>
              Anyone could send your address a message that looks like ours, so check the{' '}
              <C>HolidayHive-Signature</C> header. It is <C>t=</C> (when we sent it, in seconds) and <C>v1=</C>: the
              HMAC-SHA256, in hex, of the timestamp, a full stop and the body, using the address’s signing secret.
              Work out the same thing and compare. Use the body <strong>exactly as it arrived</strong>, before
              reading it as JSON, and refuse anything more than 5 minutes old so an old message can’t be replayed.
            </P>
            {lang === 'curl'
              ? <P>curl can’t check a signature, so here it is in Python:</P>
              : <P>In PowerShell (pass the body as bytes, exactly as received):</P>}
            <Code text={lang === 'curl' ? SIGNATURE_CHECK.python : SIGNATURE_CHECK.powershell} testid="dev-webhook-signature" />

            <H3 id="webhook-retries">Retries and duplicates</H3>
            <P>
              If your address doesn’t answer with a <C>2xx</C>, we try again after about 1 minute, 5 minutes,
              15 minutes, 1 hour, 3, 6, 12 and 24 hours — 9 tries over about two days — then give up. The company’s
              admins can see every try in Settings → Integrations and press <strong>Send again</strong>. If an address
              has failed every time for 3 days we pause it and tell the company’s admins; nothing more is sent until one
              of them turns it back on.
            </P>
            <P>
              Delivery is <strong>at least once</strong>, never exactly once: a slow answer, a retry or Send again can
              mean the same message arrives twice. Every try of the same message has the same <C>event_id</C> (also in
              the <C>HolidayHive-Event-Id</C> header), so keep the ones you’ve seen and drop repeats. Messages for one
              address are sent oldest first, but a retry can arrive after a newer message — use <C>occurred_at</C> if
              order matters.
            </P>

            <p className="mt-10 border-t border-hive-line pt-6 text-hive-muted">
              Questions? <Link to="/contact" className="text-hive-orange hover:underline">Get in touch</Link>.
            </p>
          </div>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
